import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Users, Calendar, Award, CreditCard, ArrowLeft, RefreshCw, CheckCircle, XCircle, Eye, Clock, Building, Trash2, Download, FileText, Plus, Pencil, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";

export default function AdminPage() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingFestivals, setPendingFestivals] = useState([]);
  const [allCompetitions, setAllCompetitions] = useState([]);
  const [allFestivals, setAllFestivals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    category: "Beginner",
    max_dancers: 70,
    rounds: ["Preliminary", "Quarter-Finals", "Semi-Finals", "Finals"],
    audience_voting_enabled: false,
    audience_voting_percentage: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const getAuthHeaders = () => ({
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  useEffect(() => {
    if (user?.role === "admin") {
      fetchData();
    }
  }, [user, token]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, pendingRes, compsRes, festivalsRes, allFestivalsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/admin/users`, getAuthHeaders()),
        axios.get(`${API}/admin/pending-approvals`, getAuthHeaders()),
        axios.get(`${API}/admin/all-competitions`, getAuthHeaders()),
        axios.get(`${API}/admin/pending-festivals`, getAuthHeaders()),
        axios.get(`${API}/admin/all-festivals`, getAuthHeaders()),
        axios.get(`${API}/templates`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPendingApprovals(pendingRes.data);
      const comps = compsRes.data;
      setPendingFestivals(festivalsRes.data);
      setAllFestivals(allFestivalsRes.data);
      setTemplates(templatesRes.data || []);
      
      // Fetch dancers for competitions that have them (for competitors tab)
      const compsWithDancers = comps.filter(c => c.dancer_count > 0);
      const dancerPromises = compsWithDancers.map(c => 
        axios.get(`${API}/dancers?competition_id=${c.competition_id}`, getAuthHeaders()).then(r => ({ comp_id: c.competition_id, dancers: r.data })).catch(() => ({ comp_id: c.competition_id, dancers: [] }))
      );
      const dancerResults = await Promise.all(dancerPromises);
      const dancerMap = {};
      dancerResults.forEach(r => { dancerMap[r.comp_id] = r.dancers; });
      
      // Attach dancers to competitions
      const enrichedComps = comps.map(c => ({ ...c, _dancers: dancerMap[c.competition_id] || [] }));
      setAllCompetitions(enrichedComps);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, getAuthHeaders());
      toast.success("User role updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleApprovePayment = async (competitionId) => {
    try {
      await axios.post(`${API}/competitions/${competitionId}/approve-payment`, {}, getAuthHeaders());
      toast.success("Payment approved!");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve payment");
    }
  };

  const handleRejectPayment = async (competitionId) => {
    if (!window.confirm("Are you sure you want to reject this payment?")) return;
    try {
      await axios.post(`${API}/competitions/${competitionId}/reject-payment?reason=Invalid receipt`, {}, getAuthHeaders());
      toast.success("Payment rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject payment");
    }
  };

  const handleApproveFestival = async (festivalId) => {
    try {
      await axios.post(`${API}/festivals/${festivalId}/approve`, {}, getAuthHeaders());
      toast.success("Festival approved!");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve festival");
    }
  };

  const handleRejectFestival = async (festivalId) => {
    if (!window.confirm("Are you sure you want to reject this festival?")) return;
    try {
      await axios.post(`${API}/festivals/${festivalId}/reject?reason=Invalid payment`, {}, getAuthHeaders());
      toast.success("Festival rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject festival");
    }
  };

  const handleDeleteFestival = async (festivalId) => {
    if (!window.confirm("DELETE this festival and ALL its competitions, dancers, heats, scores? This cannot be undone!")) return;
    try {
      await axios.delete(`${API}/admin/festivals/${festivalId}`, getAuthHeaders());
      toast.success("Festival and all data deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete festival");
    }
  };

  const handleDeleteCompetition = async (competitionId) => {
    if (!window.confirm("DELETE this competition and all its data? This cannot be undone!")) return;
    try {
      await axios.delete(`${API}/admin/competitions/${competitionId}`, getAuthHeaders());
      toast.success("Competition deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete competition");
    }
  };

  const openCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: "",
      category: "Beginner",
      max_dancers: 70,
      rounds: ["Preliminary", "Quarter-Finals", "Semi-Finals", "Finals"],
      audience_voting_enabled: false,
      audience_voting_percentage: 0,
    });
    setShowTemplateDialog(true);
  };

  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({
      name: t.name,
      category: t.category,
      max_dancers: t.max_dancers,
      rounds: t.rounds || ["Preliminary", "Quarter-Finals", "Semi-Finals", "Finals"],
      audience_voting_enabled: !!t.audience_voting_enabled,
      audience_voting_percentage: t.audience_voting_percentage || 0,
    });
    setShowTemplateDialog(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    try {
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.template_id}`, templateForm, getAuthHeaders());
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/templates`, templateForm, getAuthHeaders());
        toast.success("Template created");
      }
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Delete this template? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/templates/${templateId}`, getAuthHeaders());
      toast.success("Template deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete template");
    }
  };

  const handleResetUserPassword = async (u) => {
    const tempPassword = window.prompt(
      `Set a temporary password for ${u.name} (${u.email}).\n` +
      `The user will be forced to change it on next login.\n\n` +
      `Suggested: Dance2026!`,
      "Dance2026!"
    );
    if (!tempPassword) return;
    if (tempPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/users/${u.user_id}/reset-password`,
        { temp_password: tempPassword },
        getAuthHeaders()
      );
      toast.success(`Temporary password set for ${u.email}. Share it with them privately.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-[#27272a] mx-auto mb-4" />
          <h1 className="text-xl text-white mb-2">Access Denied</h1>
          <p className="text-[#A1A1AA] mb-6">You don't have permission to access this page</p>
          <Link to="/dashboard">
            <Button className="btn-gold">Go to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const statCards = [
    { label: "Total Users", value: stats?.total_users || 0, icon: <Users className="w-6 h-6" />, color: "#FFD700" },
    { label: "Festivals", value: stats?.total_festivals || 0, icon: <Calendar className="w-6 h-6" />, color: "#00E5FF" },
    { label: "Competitions", value: stats?.total_competitions || 0, icon: <Award className="w-6 h-6" />, color: "#FF007F" },
    { label: "Total Dancers", value: stats?.total_dancers || 0, icon: <Users className="w-6 h-6" />, color: "#A1A1AA" },
    { label: "Paid", value: stats?.paid_competitions || 0, icon: <CreditCard className="w-6 h-6" />, color: "#22C55E" },
    { label: "Pending Approval", value: stats?.pending_approvals || 0, icon: <Clock className="w-6 h-6" />, color: "#F59E0B" }
  ];

  const statusColors = {
    setup: "bg-[#A1A1AA]/20 text-[#A1A1AA]",
    pending_payment: "bg-[#F59E0B]/20 text-[#F59E0B]",
    pending_approval: "bg-[#FF007F]/20 text-[#FF007F]",
    registration: "bg-[#00E5FF]/20 text-[#00E5FF]",
    active: "bg-[#FF007F]/20 text-[#FF007F]",
    paused: "bg-[#FFD700]/20 text-[#FFD700]",
    completed: "bg-green-500/20 text-green-500"
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-[#A1A1AA] hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 text-[#FFD700]" />
              <span className="font-['Playfair_Display'] text-xl font-bold text-white">Admin Dashboard</span>
            </div>
          </div>
          <Button onClick={fetchData} variant="outline" className="btn-outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
              {statCards.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="glass p-5 rounded-xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex items-center gap-2 mb-2" style={{ color: stat.color }}>
                    {stat.icon}
                  </div>
                  <p className="font-['Playfair_Display'] text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-[#A1A1AA] text-xs">{stat.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Tabs */}
            <Tabs defaultValue="festivals" className="space-y-6">
              <TabsList className="bg-[#18181b] border border-white/10">
                <TabsTrigger value="festivals" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
                  <Building className="w-4 h-4 mr-2" />
                  Pending Festivals ({pendingFestivals.length})
                </TabsTrigger>
                <TabsTrigger value="pending" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
                  <Clock className="w-4 h-4 mr-2" />
                  Pending Competitions ({pendingApprovals.length})
                </TabsTrigger>
                <TabsTrigger value="competitions" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
                  <Award className="w-4 h-4 mr-2" />
                  All Competitions
                </TabsTrigger>
                <TabsTrigger value="allfestivals" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
                  <Calendar className="w-4 h-4 mr-2" />
                  All Festivals ({allFestivals.length})
                </TabsTrigger>
                <TabsTrigger value="users" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
                  <Users className="w-4 h-4 mr-2" />
                  Users
                </TabsTrigger>
                <TabsTrigger value="competitors" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
                  <Users className="w-4 h-4 mr-2" />
                  Competitors
                </TabsTrigger>
                <TabsTrigger value="templates" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black" data-testid="templates-tab">
                  <FileText className="w-4 h-4 mr-2" />
                  Templates ({templates.length})
                </TabsTrigger>
              </TabsList>

              {/* Pending Festivals Tab */}
              <TabsContent value="festivals">
                {pendingFestivals.length === 0 ? (
                  <div className="text-center py-16 glass rounded-xl">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-white text-lg">No pending festivals</p>
                    <p className="text-[#A1A1AA]">All festival payment receipts have been processed</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingFestivals.map((festival) => (
                      <motion.div
                        key={festival.festival_id}
                        className="glass p-6 rounded-xl border-l-4 border-l-[#FFD700]"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        data-testid={`pending-festival-${festival.festival_id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-white font-bold text-lg">{festival.name}</h3>
                              <span className="text-[#00E5FF] font-mono text-sm">{festival.event_code}</span>
                            </div>
                            <p className="text-[#A1A1AA] text-sm">
                              Location: {festival.location} | Date: {festival.date}
                            </p>
                            <p className="text-[#A1A1AA] text-sm">
                              Organizer: {festival.organizer_name} ({festival.organizer_email})
                            </p>
                          </div>
                          <div className="flex gap-3">
                            {festival.payment_receipt_url && (
                              <Button
                                variant="outline"
                                className="btn-outline"
                                onClick={() => setSelectedReceipt(festival.payment_receipt_url)}
                                data-testid={`view-festival-receipt-${festival.festival_id}`}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Receipt
                              </Button>
                            )}
                            <Button
                              className="bg-green-500 hover:bg-green-600 text-white"
                              onClick={() => handleApproveFestival(festival.festival_id)}
                              data-testid={`approve-festival-${festival.festival_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => handleRejectFestival(festival.festival_id)}
                              data-testid={`reject-festival-${festival.festival_id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Pending Approvals Tab */}
              <TabsContent value="pending">
                {pendingApprovals.length === 0 ? (
                  <div className="text-center py-16 glass rounded-xl">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-white text-lg">No pending approvals</p>
                    <p className="text-[#A1A1AA]">All payment receipts have been processed</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingApprovals.map((comp) => (
                      <motion.div
                        key={comp.competition_id}
                        className="glass p-6 rounded-xl border-l-4 border-l-[#F59E0B]"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        data-testid={`pending-${comp.competition_id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-white font-bold text-lg">{comp.festival_name}</h3>
                              <span className="text-[#FFD700]">{comp.category}</span>
                            </div>
                            <p className="text-[#A1A1AA] text-sm">
                              Organizer: {comp.organizer_name} ({comp.organizer_email})
                            </p>
                          </div>
                          <div className="flex gap-3">
                            {comp.payment_receipt_url && (
                              <Button
                                variant="outline"
                                className="btn-outline"
                                onClick={() => setSelectedReceipt(comp.payment_receipt_url)}
                                data-testid={`view-receipt-${comp.competition_id}`}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Receipt
                              </Button>
                            )}
                            <Button
                              className="bg-green-500 hover:bg-green-600 text-white"
                              onClick={() => handleApprovePayment(comp.competition_id)}
                              data-testid={`approve-${comp.competition_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => handleRejectPayment(comp.competition_id)}
                              data-testid={`reject-${comp.competition_id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* All Competitions Tab */}
              <TabsContent value="competitions">
                <div className="glass rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Festival</th>
                          <th>Category</th>
                          <th>Dancers</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allCompetitions.map((comp) => (
                          <tr key={comp.competition_id} data-testid={`comp-row-${comp.competition_id}`}>
                            <td className="text-white">{comp.festival_name}</td>
                            <td className="text-white">{comp.category}</td>
                            <td>
                              <span className="text-[#FFD700] font-bold">{comp.dancer_count || 0}</span>
                            </td>
                            <td>
                              <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${statusColors[comp.status] || statusColors.setup}`}>
                                {comp.status?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="text-[#A1A1AA]">
                              {comp.created_at ? new Date(comp.created_at).toLocaleDateString() : 'N/A'}
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeleteCompetition(comp.competition_id)}
                                data-testid={`delete-comp-${comp.competition_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* All Festivals Tab — Expandable festival cards with inline competitions */}
              <TabsContent value="allfestivals">
                <div className="space-y-4" data-testid="all-festivals-list">
                  {allFestivals.length === 0 && (
                    <div className="glass p-12 rounded-xl text-center">
                      <Calendar className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                      <p className="text-white text-lg">No festivals yet</p>
                    </div>
                  )}
                  {allFestivals.map((f) => {
                    const festComps = allCompetitions.filter(c => c.festival_id === f.festival_id);
                    return (
                      <details
                        key={f.festival_id}
                        className="glass rounded-xl overflow-hidden group"
                        data-testid={`festival-card-${f.festival_id}`}
                      >
                        <summary className="cursor-pointer list-none p-5 flex items-center justify-between hover:bg-white/[0.03] transition-colors">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/15 flex items-center justify-center flex-shrink-0">
                              <Calendar className="w-5 h-5 text-[#00E5FF]" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-white font-semibold text-base truncate">{f.name}</p>
                              <p className="text-[#A1A1AA] text-xs mt-0.5">
                                <span className="text-[#FFD700] font-mono">{f.event_code}</span>
                                <span className="mx-2">·</span>
                                {f.organizer_name || '—'}
                                <span className="mx-2">·</span>
                                <span className="text-white">{festComps.length}</span> competitions
                                <span className="mx-2">·</span>
                                <span className="text-[#FFD700] font-bold">{f.dancer_count || 0}</span> dancers
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                              f.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                              f.status === 'pending_approval' ? 'bg-[#FF007F]/20 text-[#FF007F]' :
                              f.status === 'pending_payment' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                              'bg-[#A1A1AA]/20 text-[#A1A1AA]'
                            }`}>
                              {f.status?.replace('_', ' ')}
                            </span>
                            <Button
                              size="sm" variant="ghost"
                              className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                              onClick={(e) => { e.preventDefault(); handleDeleteFestival(f.festival_id); }}
                              data-testid={`delete-festival-${f.festival_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <span className="text-[#52525B] text-xs ml-2 group-open:rotate-90 transition-transform">▶</span>
                          </div>
                        </summary>

                        {/* Inline competitions list */}
                        <div className="border-t border-white/5 bg-black/20 p-4">
                          {festComps.length === 0 ? (
                            <p className="text-[#71717A] text-sm italic px-2 py-3">
                              This festival has no competitions yet.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {festComps.map((c) => (
                                <div
                                  key={c.competition_id}
                                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] transition-colors border border-white/5"
                                  data-testid={`fest-comp-row-${c.competition_id}`}
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <Award className="w-4 h-4 text-[#FF007F] flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-white text-sm font-medium truncate">{c.category}</p>
                                      <p className="text-[#71717A] text-xs">
                                        <span className="text-[#FFD700] font-bold">{c.dancer_count || 0}</span> dancers
                                        <span className="mx-1.5">·</span>
                                        <span className="uppercase">{c.status?.replace('_', ' ')}</span>
                                        {c.created_at && (
                                          <>
                                            <span className="mx-1.5">·</span>
                                            {new Date(c.created_at).toLocaleDateString()}
                                          </>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-wrap justify-end">
                                    <Link to={`/competition/${c.competition_id}`}>
                                      <Button size="sm" variant="outline" className="btn-outline text-xs h-8"
                                        data-testid={`open-comp-${c.competition_id}`}>
                                        <Eye className="w-3 h-3 mr-1" /> Open
                                      </Button>
                                    </Link>
                                    <Button size="sm" variant="outline" className="btn-outline text-xs h-8"
                                      onClick={() => window.open(`${API}/export/results/${c.competition_id}`, '_blank')}
                                      data-testid={`comp-results-pdf-${c.competition_id}`}>
                                      <Download className="w-3 h-3 mr-1" /> Results
                                    </Button>
                                    <Button size="sm" variant="outline" className="btn-outline text-xs h-8"
                                      onClick={() => window.open(`${API}/export/competitors/${c.competition_id}`, '_blank')}
                                      data-testid={`comp-competitors-pdf-${c.competition_id}`}>
                                      <Users className="w-3 h-3 mr-1" /> Competitors
                                    </Button>
                                    <Button size="sm" variant="outline" className="btn-outline text-xs h-8"
                                      onClick={() => window.open(`${API}/feedback/competition/${c.competition_id}/pdf`, '_blank')}
                                      data-testid={`comp-feedback-pdf-${c.competition_id}`}>
                                      <FileText className="w-3 h-3 mr-1" /> Feedback
                                    </Button>
                                    <Button size="sm" variant="ghost"
                                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10 h-8 px-2"
                                      onClick={() => handleDeleteCompetition(c.competition_id)}
                                      data-testid={`fest-delete-comp-${c.competition_id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users">
                <div className="glass rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Created</th>
                          <th>Role</th>
                          <th>Reset Password</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                            <td>
                              <div className="flex items-center gap-3">
                                {u.picture ? (
                                  <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-[#27272a] flex items-center justify-center">
                                    <Users className="w-4 h-4 text-[#52525B]" />
                                  </div>
                                )}
                                <span className="text-white">{u.name}</span>
                              </div>
                            </td>
                            <td className="text-[#A1A1AA]">{u.email}</td>
                            <td>
                              <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                                u.role === 'admin' ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                                u.role === 'organizer' ? 'bg-[#00E5FF]/20 text-[#00E5FF]' :
                                'bg-[#A1A1AA]/20 text-[#A1A1AA]'
                              }`}>
                                {u.role}
                              </span>
                            </td>
                            <td className="text-[#A1A1AA]">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                            </td>
                            <td>
                              <Select
                                value={u.role}
                                onValueChange={(value) => handleUpdateRole(u.user_id, value)}
                                disabled={u.user_id === user?.user_id}
                              >
                                <SelectTrigger className="w-32 h-8 bg-black/20 border-white/10 text-white text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#18181b] border-white/10">
                                  <SelectItem value="viewer">Viewer</SelectItem>
                                  <SelectItem value="organizer">Organizer</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline"
                                className="btn-outline text-xs h-8"
                                onClick={() => handleResetUserPassword(u)}
                                disabled={u.user_id === user?.user_id}
                                data-testid={`reset-password-${u.user_id}`}
                              >
                                <Lock className="w-3 h-3 mr-1" />
                                Reset
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* Competitors Tab */}
              <TabsContent value="competitors">
                <div className="space-y-6">
                  {allFestivals.map(festival => {
                    const festComps = allCompetitions.filter(c => c.festival_id === festival.festival_id);
                    if (festComps.length === 0) return null;
                    const totalDancers = festComps.reduce((sum, c) => sum + (c.dancer_count || 0), 0);
                    if (totalDancers === 0) return null;
                    return (
                      <div key={festival.festival_id} className="glass rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-white/5 flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-bold text-lg">{festival.name}</h3>
                            <p className="text-[#A1A1AA] text-sm">{festival.event_code} • {totalDancers} competitors</p>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {festComps.map(c => (
                              <div key={c.competition_id} className="flex gap-1">
                                <Button size="sm" variant="outline" className="btn-outline text-xs"
                                  onClick={() => window.open(`${API}/export/competitors/${c.competition_id}`, '_blank')}>
                                  <Download className="w-3 h-3 mr-1" /> {c.category}
                                </Button>
                                <Button size="sm" variant="outline" className="btn-outline text-xs"
                                  onClick={() => window.open(`${API}/feedback/competition/${c.competition_id}/pdf`, '_blank')}
                                  data-testid={`admin-feedback-pdf-${c.competition_id}`}
                                  title="All judge feedback (PDF)">
                                  <Download className="w-3 h-3 mr-1" /> Feedback
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Competition</th>
                                <th>#</th>
                                <th>Name</th>
                                <th>Role</th>
                                <th>Country</th>
                                <th>Phone</th>
                              </tr>
                            </thead>
                            <tbody>
                              {festComps.map(comp => 
                                (comp._dancers || []).map(d => (
                                  <tr key={d.dancer_id}>
                                    <td className="text-[#A1A1AA] text-xs">{comp.category}</td>
                                    <td className="text-[#FFD700] font-bold">#{d.bib_number}</td>
                                    <td className="text-white">{d.name}</td>
                                    <td><span className={d.role === 'Leader' ? 'text-[#FFD700]' : 'text-[#FF007F]'}>{d.role}</span></td>
                                    <td className="text-[#A1A1AA]">{d.country || '-'}</td>
                                    <td className="text-[#A1A1AA]">{d.phone || '-'}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              {/* Templates Tab */}
              <TabsContent value="templates">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-['Playfair_Display'] text-xl text-white">Competition Templates</h3>
                    <p className="text-[#A1A1AA] text-sm">Reusable presets that organizers can apply when creating competitions.</p>
                  </div>
                  <Button onClick={openCreateTemplate} className="btn-gold" data-testid="create-template-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Template
                  </Button>
                </div>

                {templates.length === 0 ? (
                  <div className="text-center py-16 glass rounded-xl">
                    <FileText className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                    <p className="text-white text-lg">No templates yet</p>
                    <p className="text-[#A1A1AA]">Create your first template to speed up competition setup.</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {templates.map((t) => (
                      <motion.div
                        key={t.template_id}
                        className="glass p-5 rounded-xl border border-white/5 hover:border-[#FFD700]/40 transition-colors"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        data-testid={`template-card-${t.template_id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-white font-bold text-lg leading-tight">{t.name}</h4>
                            <span className="text-[#FFD700] text-xs font-mono uppercase tracking-wider">{t.category}</span>
                          </div>
                          <FileText className="w-5 h-5 text-[#FFD700]" />
                        </div>
                        <div className="space-y-1 text-sm mb-4">
                          <div className="flex justify-between text-[#A1A1AA]">
                            <span>Max Dancers</span>
                            <span className="text-white font-mono">{t.max_dancers}</span>
                          </div>
                          <div className="flex justify-between text-[#A1A1AA]">
                            <span>Rounds</span>
                            <span className="text-white font-mono">{(t.rounds || []).length}</span>
                          </div>
                          <div className="flex justify-between text-[#A1A1AA]">
                            <span>Audience Voting</span>
                            <span className={t.audience_voting_enabled ? "text-green-400" : "text-[#52525B]"}>
                              {t.audience_voting_enabled ? `${t.audience_voting_percentage}%` : "Off"}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 pt-3 border-t border-white/5">
                          <Button
                            size="sm"
                            variant="outline"
                            className="btn-outline flex-1"
                            onClick={() => openEditTemplate(t)}
                            data-testid={`edit-template-${t.template_id}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                            onClick={() => handleDeleteTemplate(t.template_id)}
                            data-testid={`delete-template-${t.template_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>

      {/* Template Editor Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-2xl text-white">
              {editingTemplate ? "Edit Template" : "New Template"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTemplate} className="space-y-4 mt-4">
            <div>
              <Label className="text-[#A1A1AA]">Template Name</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                className="mt-1 bg-black/20 border-white/10 text-white"
                placeholder="e.g. Pro Jack & Jill"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Category</Label>
              <Select
                value={templateForm.category}
                onValueChange={(value) => setTemplateForm({ ...templateForm, category: value })}
              >
                <SelectTrigger className="mt-1 bg-black/20 border-white/10 text-white" data-testid="template-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10">
                  <SelectItem value="Beginner">Beginner</SelectItem>
                  <SelectItem value="Intermediate">Intermediate</SelectItem>
                  <SelectItem value="Advanced">Advanced</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Max Dancers</Label>
              <Input
                type="number"
                min="1"
                max="500"
                value={templateForm.max_dancers}
                onChange={(e) => setTemplateForm({ ...templateForm, max_dancers: parseInt(e.target.value) || 0 })}
                className="mt-1 bg-black/20 border-white/10 text-white"
                data-testid="template-max-dancers-input"
              />
            </div>
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-white">Audience Voting</Label>
                  <p className="text-[#A1A1AA] text-xs">Allow audience to vote for dancers</p>
                </div>
                <Switch
                  checked={templateForm.audience_voting_enabled}
                  onCheckedChange={(checked) => setTemplateForm({
                    ...templateForm,
                    audience_voting_enabled: checked,
                    audience_voting_percentage: checked ? 10 : 0,
                  })}
                  data-testid="template-audience-voting-switch"
                />
              </div>
              {templateForm.audience_voting_enabled && (
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#A1A1AA]">Audience Score Weight</span>
                    <span className="text-[#FFD700] font-bold">{templateForm.audience_voting_percentage}%</span>
                  </div>
                  <Slider
                    value={[templateForm.audience_voting_percentage]}
                    onValueChange={(v) => setTemplateForm({ ...templateForm, audience_voting_percentage: v[0] })}
                    max={50}
                    min={5}
                    step={5}
                    className="mb-2"
                    data-testid="template-audience-percentage-slider"
                  />
                  <p className="text-[#52525B] text-xs">
                    Judges: {100 - templateForm.audience_voting_percentage}% | Audience: {templateForm.audience_voting_percentage}%
                  </p>
                </div>
              )}
            </div>
            <Button type="submit" className="w-full btn-gold py-3" data-testid="save-template-btn">
              {editingTemplate ? "Save Changes" : "Create Template"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={!!selectedReceipt} onOpenChange={() => setSelectedReceipt(null)}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Payment Receipt</DialogTitle>
          </DialogHeader>
          {selectedReceipt && (
            <div className="mt-4">
              <img 
                src={selectedReceipt.startsWith('data:') ? selectedReceipt : `data:image/png;base64,${selectedReceipt}`} 
                alt="Payment Receipt" 
                className="w-full rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
